// =====================================================================
// db/index.ts — Main API surface for the app
// =====================================================================
// Usage from UI components:
//
//   import { db } from '@/db';
//
//   const posts = await db.posts.list({ limit: 50 });
//   await db.posts.create({ type: 'status', body: 'Great round today' });
//   await db.follows.add(memberId);
//   await db.callShots.settle(shotId, 78);
//
// All methods return promises. Errors throw — components should
// catch with try/catch or use Suspense/error boundaries.
//
// RLS handles authorization. If a member tries to read a post they
// can't see, the result is simply not included (not an error).
// =====================================================================

import {
  clearMemberCache,
  getCachedMember,
  patchMemberCache,
  refreshMember,
  subscribeAuthChanges,
} from './auth-session';
import { getSupabase } from './client';
import { callEdgeFunction } from '../edge';
import { filterProductionCourses } from '@/lib/courses/filter';
import { memberDisplayName, normalizeMemberPublic } from '@/lib/display-name';
import { clampCallTarget, expectedScoreForCourse } from '@/lib/shots/expected-score';
import { isValidCallActualScore, pickPendingCallToSettle } from '@/lib/shots/call-shot-settle';
import { validateRoundGross } from '@/lib/scoring/round-gross';
import { persistAuthStorageIfSupported, normalizeEmailOtp } from '@/lib/auth-otp';
import { persistInviteToken } from '@/lib/invite-token';
import { groupRounds, playerKeyFor } from './group-rounds';
import { formatJoinError } from './join-errors';
import type {
  UUID, ISODateString, Member, MemberPublic, MemberWithStats,
  Post, PostEnriched, PostType, PostMetadata, PostMedia,
  Reaction, ReactionEmoji,
  Comment,
  Round, RoundHole, HoleRange, TickerScore,
  Game, GameParticipant,
  LiveBet, PostWager, PostLike,
  Tournament, TournamentParticipant,
  CallYourShot, CallShotResolution,
  Ace, Achievement, PointsEntry, CharityDonation,
  Notification, Suggestion, SuggestionCategory, SuggestionStatus,
  Course, CourseTee, CourseHole,
  Club,
  Visibility,
  ServiceBranch, ServiceStatus,
  Invitation,
} from './types';

// =====================================================================
// AUTH
// =====================================================================

export const auth = {
  /** Request a 6-digit email OTP (and optional magic link if template includes both). */
  async requestEmailOtp(
    email: string,
    opts: { shouldCreateUser: boolean; emailRedirectTo?: string },
  ): Promise<void> {
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const { error } = await getSupabase().auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        emailRedirectTo: opts.emailRedirectTo ?? origin,
        shouldCreateUser: opts.shouldCreateUser,
      },
    });
    if (error) throw error;
  },

  /** Returning members only — does not create new auth.users rows. */
  async signIn(email: string): Promise<void> {
    await auth.requestEmailOtp(email, { shouldCreateUser: false });
  },

  /** New invite onboarding — OTP in-app; magic link fallback to /signup. */
  async signInForOnboarding(email: string, inviteToken: string): Promise<void> {
    persistInviteToken(inviteToken);
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    await auth.requestEmailOtp(email, {
      shouldCreateUser: true,
      emailRedirectTo: `${origin}/signup`,
    });
  },

  /** Verify email OTP and establish a session in this browser/PWA context. */
  async verifyEmailOtp(email: string, token: string): Promise<void> {
    const otp = normalizeEmailOtp(token);
    if (otp.length !== 6) {
      throw new Error('Enter the 6-digit code from your email.');
    }
    const { data, error } = await getSupabase().auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: otp,
      type: 'email',
    });
    if (error) throw error;
    if (!data.session) {
      throw new Error('Verification failed. Request a new code and try again.');
    }
    await persistAuthStorageIfSupported();
    clearMemberCache();
    await refreshMember();
  },

  async signOut(): Promise<void> {
    clearMemberCache();
    const { error } = await getSupabase().auth.signOut();
    if (error) throw error;
  },

  /** Returns the current member (cached + getSession fast path). */
  async me(): Promise<Member | null> {
    return getCachedMember();
  },

  /** Validates JWT with Supabase — use on app bootstrap and explicit retry. */
  async refresh(): Promise<Member | null> {
    return refreshMember();
  },

  /** Subscribe to auth state changes (sign in/out). */
  onChange(cb: (member: Member | null) => void): () => void {
    return subscribeAuthChanges(cb);
  },
};

/** Optional RPC — returns empty data when the function is missing or errors. */
async function optionalRpc<T>(
  call: () => PromiseLike<{ data: T | null; error: unknown }>,
): Promise<{ data: T | null }> {
  try {
    const result = await call();
    if (result.error) return { data: null };
    return { data: result.data };
  } catch {
    return { data: null };
  }
}

// =====================================================================
// MEMBERS (the directory)
// =====================================================================

export const members = {
  /** List members in the same club. Searchable by name/handle (via tsvector index). */
  async list(opts: { search?: string; limit?: number } = {}): Promise<MemberPublic[]> {
    let q = getSupabase()
      .from('members')
      .select('id, club_id, name, handle, avatar, profile_photo_url, about_me, service_branch, service_rank, service_status, handicap, handicap_updated_at, visibility, hide_service, venmo, created_at')
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('name');

    if (opts.search) {
      q = q.or(`name.ilike.%${opts.search}%,handle.ilike.%${opts.search}%`);
    }
    if (opts.limit) q = q.limit(opts.limit);
    const { data, error } = await q;
    if (error) throw error;
    return (data as MemberPublic[]).map(normalizeMemberPublic);
  },

  async get(id: UUID): Promise<MemberPublic | null> {
    const { data, error } = await getSupabase()
      .from('members')
      .select('id, club_id, name, handle, avatar, profile_photo_url, about_me, service_branch, service_rank, service_status, handicap, handicap_updated_at, visibility, hide_service, venmo, created_at')
      .eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return normalizeMemberPublic(data as MemberPublic);
  },

  /** Batch-fetch stats for many members (6 queries total — used by Board). */
  async listWithStats(opts: { search?: string; limit?: number } = {}): Promise<MemberWithStats[]> {
    const people = await this.list(opts);
    if (!people.length) return [];

    const ids = people.map((p) => p.id);
    const sb = getSupabase();

    const [pointsRpc, roundsRpc, points, rounds, aces, follow, giving, lowGross] = await Promise.all([
      optionalRpc(() => sb.rpc('club_points_totals')),
      optionalRpc(() => sb.rpc('club_round_stats')),
      sb.from('v_points_totals').select('member_id, total_points').in('member_id', ids),
      sb.from('v_rounds_stats').select('member_id, rounds_count, avg_gross').in('member_id', ids),
      sb.from('v_aces_count').select('member_id, ace_count').in('member_id', ids),
      sb.from('v_follow_counts').select('member_id, follower_count, following_count').in('member_id', ids),
      sb.from('v_giving_totals').select('member_id, total_cents').in('member_id', ids),
      sb.from('v_low_gross').select('member_id, low_gross').in('member_id', ids),
    ]);

    for (const res of [points, rounds, aces, follow, giving, lowGross]) {
      if (res.error) throw res.error;
    }

    const pointsMap = Object.fromEntries((points.data || []).map((r: { member_id: UUID; total_points: number }) => [r.member_id, r.total_points]));
    const roundsViewMap = Object.fromEntries((rounds.data || []).map((r: { member_id: UUID; rounds_count: number; avg_gross: number | null }) => [r.member_id, r]));
    const pointsRpcMap = Object.fromEntries((pointsRpc.data || []).map((r: { member_id: UUID; total_points: number }) => [r.member_id, r.total_points]));
    const roundsRpcMap = Object.fromEntries((roundsRpc.data || []).map((r: { member_id: UUID; rounds_count: number; avg_gross: number | null; low_gross?: number | null }) => [r.member_id, r]));
    const acesMap = Object.fromEntries((aces.data || []).map((r: { member_id: UUID; ace_count: number }) => [r.member_id, r.ace_count]));
    const followMap = Object.fromEntries((follow.data || []).map((r: { member_id: UUID; follower_count: number; following_count: number }) => [r.member_id, r]));
    const givingMap = Object.fromEntries((giving.data || []).map((r: { member_id: UUID; total_cents: number }) => [r.member_id, r.total_cents]));
    const lowMap = Object.fromEntries((lowGross.data || []).map((r: { member_id: UUID; low_gross: number }) => [r.member_id, r.low_gross]));

    return people.map((p) => {
      const rpcRound = roundsRpcMap[p.id];
      const viewRound = roundsViewMap[p.id];
      return {
        ...p,
        total_points: pointsRpcMap[p.id] ?? pointsMap[p.id] ?? 0,
        rounds_count: rpcRound?.rounds_count ?? viewRound?.rounds_count ?? 0,
        avg_gross: rpcRound?.avg_gross ?? viewRound?.avg_gross ?? null,
        low_gross: rpcRound?.low_gross ?? lowMap[p.id] ?? null,
        ace_count: acesMap[p.id] ?? 0,
        follower_count: followMap[p.id]?.follower_count ?? 0,
        following_count: followMap[p.id]?.following_count ?? 0,
        giving_total_cents: givingMap[p.id] ?? 0,
      };
    });
  },

  /** Member's full profile + aggregate stats (rounds, points, aces, etc). */
  async getWithStats(id: UUID): Promise<MemberWithStats | null> {
    const base = await this.get(id);
    if (!base) return null;
    const zeros: Omit<MemberWithStats, keyof MemberPublic> = {
      total_points: 0,
      rounds_count: 0,
      avg_gross: null,
      low_gross: null,
      ace_count: 0,
      follower_count: 0,
      following_count: 0,
      giving_total_cents: 0,
    };
    try {
      const sb = getSupabase();
      const [points, rounds, aces, follow, giving, lowGross] = await Promise.all([
        sb.from('v_points_totals').select('total_points').eq('member_id', id).maybeSingle(),
        sb.from('v_rounds_stats').select('rounds_count, avg_gross').eq('member_id', id).maybeSingle(),
        sb.from('v_aces_count').select('ace_count').eq('member_id', id).maybeSingle(),
        sb.from('v_follow_counts').select('follower_count, following_count').eq('member_id', id).maybeSingle(),
        sb.from('v_giving_totals').select('total_cents').eq('member_id', id).maybeSingle(),
        sb.from('v_low_gross').select('low_gross').eq('member_id', id).maybeSingle(),
      ]);
      for (const res of [points, rounds, aces, follow, giving, lowGross]) {
        if (res.error) throw res.error;
      }
      return {
        ...base,
        total_points: points.data?.total_points ?? 0,
        rounds_count: rounds.data?.rounds_count ?? 0,
        avg_gross: rounds.data?.avg_gross ?? null,
        low_gross: lowGross.data?.low_gross ?? null,
        ace_count: aces.data?.ace_count ?? 0,
        follower_count: follow.data?.follower_count ?? 0,
        following_count: follow.data?.following_count ?? 0,
        giving_total_cents: giving.data?.total_cents ?? 0,
      };
    } catch {
      return { ...base, ...zeros };
    }
  },

  /** Update current member's own profile. RLS enforces self-only. */
  async update(patch: Partial<Pick<Member,
    | 'name' | 'handle' | 'avatar' | 'profile_photo_url' | 'about_me'
    | 'service_branch' | 'service_rank' | 'service_status'
    | 'handicap' | 'ghin_number' | 'ghin_connected' | 'venmo' | 'visibility' | 'hide_service' | 'phone'
  >>): Promise<Member> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const updates: Record<string, unknown> = { ...patch };
    if (patch.handicap !== undefined) updates.handicap_updated_at = new Date().toISOString();
    const { data, error } = await getSupabase()
      .from('members').update(updates).eq('id', me.id).select().single();
    if (error) throw error;
    const updated = { ...(data as Member), name: memberDisplayName(data as Member) };
    patchMemberCache(updated);
    return updated;
  },

  async setVisibility(v: Visibility): Promise<void> {
    await this.update({ visibility: v });
  },

  async setService(s: { branch: ServiceBranch | null; rank: string | null; status: ServiceStatus | null }): Promise<void> {
    await this.update({
      service_branch: s.branch,
      service_rank: s.rank,
      service_status: s.status,
    });
  },
};

// =====================================================================
// FOLLOWS
// =====================================================================

function isDuplicateRowError(error: { code?: string; status?: number; message?: string }): boolean {
  return error.code === '23505'
    || error.status === 409
    || /duplicate|unique|already exists/i.test(error.message || '');
}

async function insertNotification(input: {
  recipient_id: UUID;
  actor_id: UUID;
  type: Notification['type'];
  subject_type?: string | null;
  subject_id?: UUID | null;
  body?: string | null;
}): Promise<void> {
  if (input.recipient_id === input.actor_id) return;
  const { error } = await getSupabase().from('notifications').insert(input);
  if (error && !isDuplicateRowError(error)) {
    console.warn('notification insert failed', error.message);
  }
}

async function notifyPostAuthor(postId: UUID, actorId: UUID, type: 'reaction' | 'comment'): Promise<void> {
  const { data: post } = await getSupabase()
    .from('posts').select('author_id').eq('id', postId).maybeSingle();
  if (!post?.author_id) return;
  await insertNotification({
    recipient_id: post.author_id,
    actor_id: actorId,
    type,
    subject_type: 'post',
    subject_id: postId,
  });
}

export const follows = {
  /** Returns true if a new follow row was created. */
  async add(followeeId: UUID): Promise<boolean> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    if (await this.isFollowing(followeeId)) return false;
    const { error } = await getSupabase()
      .from('follows').insert({ follower_id: me.id, followee_id: followeeId });
    if (error) {
      if (isDuplicateRowError(error)) return false;
      throw error;
    }
    void insertNotification({
      recipient_id: followeeId,
      actor_id: me.id,
      type: 'follow',
      subject_type: 'member',
      subject_id: me.id,
    });
    return true;
  },

  async remove(followeeId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('follows').delete()
      .eq('follower_id', me.id).eq('followee_id', followeeId);
    if (error) throw error;
  },

  /** Returns the IDs of members the current member follows. */
  async following(): Promise<UUID[]> {
    const me = await auth.me();
    if (!me) return [];
    const { data, error } = await getSupabase()
      .from('follows').select('followee_id').eq('follower_id', me.id);
    if (error) throw error;
    return (data || []).map(r => r.followee_id);
  },

  async followers(memberId: UUID): Promise<UUID[]> {
    const { data, error } = await getSupabase()
      .from('follows').select('follower_id').eq('followee_id', memberId);
    if (error) throw error;
    return (data || []).map(r => r.follower_id);
  },

  async isFollowing(followeeId: UUID): Promise<boolean> {
    const me = await auth.me();
    if (!me) return false;
    const { data, error } = await getSupabase()
      .from('follows').select('follower_id')
      .eq('follower_id', me.id).eq('followee_id', followeeId).maybeSingle();
    if (error) throw error;
    return data != null;
  },
};

// =====================================================================
// POSTS (the feed)
// =====================================================================

export const posts = {
  /** Main feed query. RLS filters out posts you can't see based on author visibility. */
  async list(opts: { limit?: number; before?: ISODateString; authorId?: UUID; id?: UUID; roundIds?: UUID[]; includeCourseHoles?: boolean } = {}): Promise<PostEnriched[]> {
    const sb = getSupabase();
    let q = sb.from('posts')
      .select(`
        *,
        author:members!posts_author_id_fkey (
          id, club_id, name, handle, avatar, profile_photo_url, about_me,
          service_branch, service_rank, service_status, handicap, visibility,
          hide_service, venmo, created_at
        ),
        round:rounds (
          id, course_id, tee_id, holes, gross_total, net_total, played_at, notes, posted_handicap,
          course:courses (id, slug, name, par, hole_count),
          tee:course_tees (id, name, course_rating, slope),
          round_holes (hole_num, strokes, net_strokes)
        ),
        game:games (id, format_id, name, holes, course_id, config, wager_cents, status),
        tournament:tournaments (id, name, format),
        ace:aces (id, hole_num, course_id, played_at),
        achievement:achievements (id, achievement_id, year),
        my_reactions:reactions!reactions_post_id_fkey (emoji, member_id),
        post_likes (member_id),
        post_media (id, post_id, storage_url, sort_order, created_at),
        wager:post_wagers (*)
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (opts.id)       q = q.eq('id', opts.id);
    if (opts.authorId) q = q.eq('author_id', opts.authorId);
    if (opts.roundIds?.length) q = q.in('round_id', opts.roundIds);
    if (opts.before)   q = q.lt('created_at', opts.before);
    q = q.limit(opts.limit ?? 50);

    const { data, error } = await q;
    if (error) throw error;

    const me = await auth.me();
    const posts = (data || []) as any[];
    const includeCourseHoles = opts.includeCourseHoles !== false;
    const courseIds = includeCourseHoles
      ? [...new Set(posts.map((p) => p.round?.course_id).filter(Boolean))]
      : [];
    const holesByCourse: Record<string, CourseHole[]> = {};
    if (courseIds.length) {
      await Promise.all(courseIds.map(async (cid) => {
        holesByCourse[cid] = await courses.holes(cid);
      }));
    }

    const taggedIds = [...new Set(posts.flatMap((p) => (p.metadata?.tagged || []) as string[]))];
    const taggedMembers: Record<string, MemberPublic> = {};
    if (taggedIds.length) {
      const { data: tm } = await sb.from('members')
        .select('id, club_id, name, handle, avatar, profile_photo_url, about_me, service_branch, service_rank, service_status, handicap, handicap_updated_at, visibility, hide_service, venmo, created_at')
        .in('id', taggedIds);
      (tm || []).forEach((m) => { taggedMembers[m.id] = normalizeMemberPublic(m as MemberPublic); });
    }

    const postIds = posts.map((p) => p.id);
    const commentCountByPost: Record<string, number> = {};
    const reactionCountByPost: Record<string, number> = {};
    if (postIds.length) {
      const [{ data: commentRows }, { data: reactionRows }] = await Promise.all([
        sb.from('comments').select('post_id').in('post_id', postIds).is('deleted_at', null),
        sb.from('reactions').select('post_id').in('post_id', postIds),
      ]);
      (commentRows || []).forEach((r: { post_id: string }) => {
        commentCountByPost[r.post_id] = (commentCountByPost[r.post_id] || 0) + 1;
      });
      (reactionRows || []).forEach((r: { post_id: string }) => {
        reactionCountByPost[r.post_id] = (reactionCountByPost[r.post_id] || 0) + 1;
      });
    }

    return posts.map((p) => {
      const round = p.round ? {
        ...p.round,
        course_holes: p.round.course_id ? holesByCourse[p.round.course_id] : [],
      } : undefined;
      const wagerRow = Array.isArray(p.wager) ? p.wager[0] : p.wager;
      const tagged = ((p.metadata?.tagged || []) as string[])
        .map((id: string) => taggedMembers[id])
        .filter(Boolean);
      const author = p.author ? normalizeMemberPublic(p.author as MemberPublic) : p.author;
      return {
        ...p,
        author,
        metadata: p.metadata || {},
        media: ((p.post_media || []) as PostMedia[]).sort((a, b) => a.sort_order - b.sort_order),
        round,
        wager: wagerRow || null,
        like_count: (p.post_likes || []).length,
        comment_count: Math.max(p.comment_count ?? 0, commentCountByPost[p.id] ?? 0),
        reaction_count: Math.max(p.reaction_count ?? 0, reactionCountByPost[p.id] ?? 0),
        my_liked: !!(me && (p.post_likes || []).some((l: PostLike) => l.member_id === me.id)),
        tagged_members: tagged,
        my_reactions: (p.my_reactions || [])
          .filter((r: any) => r.member_id === me?.id)
          .map((r: any) => r.emoji),
      };
    }) as PostEnriched[];
  },

  async get(id: UUID): Promise<PostEnriched | null> {
    const [p] = await this.list({ id, limit: 1 });
    return p ?? null;
  },

  /** Round posts authored by member plus group rounds they played in (tagged on poster's round). */
  async listMemberRoundHistory(
    memberId: UUID,
    opts: { limit?: number; includeCourseHoles?: boolean } = {},
  ): Promise<PostEnriched[]> {
    const limit = opts.limit ?? 80;
    const me = await auth.me();
    const taggedRoundIds = await roundTags.listRoundIdsForMember(memberId);
    const [authored, participated] = await Promise.all([
      this.list({ authorId: memberId, limit, includeCourseHoles: opts.includeCourseHoles }),
      taggedRoundIds.length
        ? this.list({
            roundIds: taggedRoundIds,
            limit: taggedRoundIds.length,
            includeCourseHoles: opts.includeCourseHoles,
          })
        : Promise.resolve([] as PostEnriched[]),
    ]);
    const authoredIds = new Set(authored.map((p) => p.id));
    const extra = participated.filter(
      (p) => p.type === 'round' && p.round && !authoredIds.has(p.id) && p.author_id !== memberId,
    );
    let merged = [...authored, ...extra]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
    if (me) {
      const { data: hiddenRows } = await getSupabase()
        .from('member_hidden_round_posts')
        .select('post_id')
        .eq('member_id', me.id);
      const hiddenIds = new Set((hiddenRows || []).map((r) => String(r.post_id)));
      merged = merged.filter((p) => !hiddenIds.has(p.id));
    }
    return merged;
  },

  async hideRoundFromMyHistory(postId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const post = await this.get(postId);
    if (!post || post.type !== 'round') throw new Error('Round post not found');
    const isParticipant = post.author_id === me.id || (post.metadata?.tagged || []).includes(me.id);
    if (!isParticipant) throw new Error('You can only hide rounds from your own history');
    const { error } = await getSupabase()
      .from('member_hidden_round_posts')
      .upsert({ member_id: me.id, post_id: postId }, { onConflict: 'member_id,post_id' });
    if (error) throw error;
  },

  async create(input: {
    type: PostType;
    body?: string;
    photo_url?: string | null;
    media_urls?: string[];
    round_id?: UUID;
    tournament_id?: UUID;
    game_id?: UUID;
    ace_id?: UUID;
    achievement_id?: UUID;
    metadata?: PostMetadata;
  }): Promise<Post> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const sb = getSupabase();
    const { media_urls, ...postInput } = input;
    const { data, error } = await sb
      .from('posts')
      .insert({ author_id: me.id, metadata: input.metadata || {}, ...postInput })
      .select().single();
    if (error) throw error;
    if (media_urls?.length) {
      const rows = media_urls.map((storage_url, sort_order) => ({
        post_id: data.id,
        storage_url,
        sort_order,
      }));
      const { error: mediaErr } = await sb.from('post_media').insert(rows);
      if (mediaErr) {
        await sb.from('posts').delete().eq('id', data.id);
        throw new Error('Media could not be attached to your post. Please try again.');
      }
    }
    return data as Post;
  },

  async update(id: UUID, patch: { body?: string; photo_url?: string | null }): Promise<Post> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('posts').update(patch).eq('id', id).eq('author_id', me.id).select().single();
    if (error) throw error;
    return data as Post;
  },

  /** Soft delete. */
  async remove(id: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('posts').update({ deleted_at: new Date().toISOString() })
      .eq('id', id).eq('author_id', me.id);
    if (error) throw error;
  },

  /** Live subscription — new posts inserted to the feed. */
  subscribe(cb: (post: Post) => void): () => void {
    const ch = getSupabase()
      .channel('posts-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'posts' },
          (payload) => cb(payload.new as Post))
      .subscribe();
    return () => { getSupabase().removeChannel(ch); };
  },
};

// =====================================================================
// POST LIKES (heart / double-tap)
// =====================================================================

export const likes = {
  async toggle(postId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const sb = getSupabase();
    const { data: existing } = await sb.from('post_likes').select('post_id')
      .eq('post_id', postId).eq('member_id', me.id).maybeSingle();
    if (existing) {
      const { error } = await sb.from('post_likes').delete()
        .eq('post_id', postId).eq('member_id', me.id);
      if (error) throw error;
    } else {
      const { error } = await sb.from('post_likes').insert({ post_id: postId, member_id: me.id });
      if (error) throw error;
    }
  },
};

// =====================================================================
// POST WAGERS
// =====================================================================

export const wagers = {
  async create(input: {
    post_id: UUID;
    to_member_id: UUID;
    amount_cents: number;
    note?: string;
  }): Promise<PostWager> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('post_wagers')
      .insert({ from_member_id: me.id, status: 'pending', ...input })
      .select().single();
    if (error) throw error;
    return data as PostWager;
  },

  async markPaid(id: UUID): Promise<void> {
    const { error } = await getSupabase()
      .from('post_wagers')
      .update({ status: 'paid', paid_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },
};

// =====================================================================
// ROUND TAGS
// =====================================================================

export const roundTags = {
  async set(roundId: UUID, memberIds: UUID[]): Promise<void> {
    const sb = getSupabase();
    await sb.from('round_tags').delete().eq('round_id', roundId);
    if (!memberIds.length) return;
    const { error } = await sb.from('round_tags')
      .insert(memberIds.map((member_id) => ({ round_id: roundId, member_id })));
    if (error) throw error;
  },

  async list(roundId: UUID): Promise<UUID[]> {
    const { data, error } = await getSupabase()
      .from('round_tags').select('member_id').eq('round_id', roundId);
    if (error) throw error;
    return (data || []).map((r) => r.member_id);
  },

  async listRoundIdsForMember(memberId: UUID): Promise<UUID[]> {
    const { data, error } = await getSupabase()
      .from('round_tags')
      .select('round_id')
      .eq('member_id', memberId);
    if (error) throw error;
    return (data || []).map((r) => r.round_id);
  },
};

// =====================================================================
// REACTIONS
// =====================================================================

export const reactions = {
  /** Returns true if the reaction was added, false if removed. */
  async toggle(postId: UUID, emoji: ReactionEmoji): Promise<boolean> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const sb = getSupabase();
    const { data: existing } = await sb.from('reactions').select('id')
      .eq('post_id', postId).eq('member_id', me.id).eq('emoji', emoji).maybeSingle();
    if (existing) {
      const { error } = await sb.from('reactions').delete().eq('id', existing.id);
      if (error) throw error;
      return false;
    }
    const { error } = await sb.from('reactions')
      .insert({ post_id: postId, member_id: me.id, emoji });
    if (error) throw error;
    void notifyPostAuthor(postId, me.id, 'reaction');
    return true;
  },

  async list(postId: UUID): Promise<Reaction[]> {
    const { data, error } = await getSupabase()
      .from('reactions').select('*').eq('post_id', postId);
    if (error) throw error;
    return (data || []) as Reaction[];
  },
};

// =====================================================================
// COMMENTS
// =====================================================================

export const comments = {
  async list(postId: UUID): Promise<Comment[]> {
    const { data, error } = await getSupabase()
      .from('comments').select('*')
      .eq('post_id', postId).is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []) as Comment[];
  },

  async add(postId: UUID, body: string): Promise<Comment> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('comments').insert({ post_id: postId, author_id: me.id, body }).select().single();
    if (error) throw error;
    void notifyPostAuthor(postId, me.id, 'comment');
    return data as Comment;
  },

  async remove(id: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('comments').update({ deleted_at: new Date().toISOString() })
      .eq('id', id).eq('author_id', me.id);
    if (error) throw error;
  },
};

// =====================================================================
// ROUNDS
// =====================================================================

export const rounds = {
  async list(opts: { memberId?: UUID; courseId?: UUID; limit?: number } = {}): Promise<Round[]> {
    let q = getSupabase().from('rounds').select('*')
      .is('deleted_at', null)
      .order('played_at', { ascending: false });
    if (opts.memberId) q = q.eq('member_id', opts.memberId);
    if (opts.courseId) q = q.eq('course_id', opts.courseId);
    if (opts.limit) q = q.limit(opts.limit);
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as Round[];
  },

  async create(input: {
    course_id: UUID;
    tee_id?: UUID;
    holes: HoleRange;
    gross_total?: number;
    net_total?: number;
    played_at?: ISODateString;
    notes?: string;
    tournament_id?: UUID;
    game_id?: UUID;
    hole_scores?: { hole_num: number; strokes: number; putts?: number }[];
  }): Promise<Round> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { hole_scores, ...roundData } = input;
    if (roundData.gross_total != null) {
      validateRoundGross({
        gross: roundData.gross_total,
        holes: roundData.holes,
        holesScored: hole_scores?.length,
      });
    }
    const { data: round, error } = await getSupabase()
      .from('rounds').insert({
        member_id: me.id,
        played_at: roundData.played_at || new Date().toISOString(),
        posted_handicap: me.handicap,
        ...roundData,
      }).select().single();
    if (error) throw error;
    if (hole_scores?.length) {
      await getSupabase().from('round_holes')
        .insert(hole_scores.map(h => ({ round_id: round.id, ...h })));
    }
    return round as Round;
  },

  async getHoles(roundId: UUID): Promise<RoundHole[]> {
    const { data, error } = await getSupabase()
      .from('round_holes').select('*').eq('round_id', roundId).order('hole_num');
    if (error) throw error;
    return (data || []) as RoundHole[];
  },

  async todaysScores(limit = 50): Promise<TickerScore[]> {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();

    const { data, error } = await getSupabase()
      .from('rounds')
      .select(`
        id,
        member_id,
        course_id,
        gross_total,
        net_total,
        holes,
        played_at,
        created_at,
        member:members!rounds_member_id_fkey ( name ),
        course:courses!rounds_course_id_fkey ( slug, name )
      `)
      .gte('played_at', startOfDay)
      .lt('played_at', endOfDay)
      .not('gross_total', 'is', null)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).map((r: {
      id: UUID;
      member_id: UUID;
      course_id: UUID;
      gross_total: number;
      net_total: number | null;
      holes: HoleRange;
      played_at: ISODateString;
      created_at: ISODateString;
      member: { name: string } | { name: string }[] | null;
      course: { slug: string; name: string } | { slug: string; name: string }[] | null;
    }): TickerScore => {
      const member = Array.isArray(r.member) ? r.member[0] : r.member;
      const course = Array.isArray(r.course) ? r.course[0] : r.course;
      return {
        id: r.id,
        member_id: r.member_id,
        member_name: member ? memberDisplayName(member) : 'Anonymous',
        course_id: r.course_id,
        course_slug: course?.slug ?? '',
        course_name: course?.name ?? '',
        gross_total: r.gross_total,
        net_total: r.net_total,
        holes: r.holes,
        played_at: r.played_at,
        created_at: r.created_at,
      };
    });
  },

  subscribeNew(cb: (round: Round) => void): () => void {
    const ch = getSupabase()
      .channel('rounds-ticker')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'rounds' },
        (payload) => cb(payload.new as Round))
      .subscribe();
    return () => { getSupabase().removeChannel(ch); };
  },
};

// =====================================================================
// GAMES
// =====================================================================

export const games = {
  async list(): Promise<Game[]> {
    const { data, error } = await getSupabase()
      .from('games').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Game[];
  },

  async get(id: UUID): Promise<Game | null> {
    const { data, error } = await getSupabase()
      .from('games').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as Game;
  },

  async create(input: Omit<Game, 'id' | 'created_at' | 'updated_at' | 'organizer_id'> & {
    participants?: UUID[];
    guests?: { name: string; handicap?: number }[];
    organizer_playing?: boolean;
  }): Promise<Game> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { participants, guests, organizer_playing, ...gameData } = input as {
      participants?: UUID[];
      guests?: { name: string; handicap?: number }[];
      organizer_playing?: boolean;
    } & Omit<Game, 'id' | 'created_at' | 'updated_at' | 'organizer_id'>;
    const organizerPlaying = organizer_playing !== false;
    const { data: game, error } = await getSupabase()
      .from('games').insert({ organizer_id: me.id, ...gameData }).select().single();
    if (error) throw error;
    const rows: Array<Record<string, unknown>> = [
      ...(organizerPlaying ? [{ game_id: game.id, member_id: me.id }] : []),
      ...(participants || []).filter((p: UUID) => p !== me.id).map((p: UUID) => ({ game_id: game.id, member_id: p })),
      ...(guests || []).map((g: { name: string; handicap?: number }) => ({ game_id: game.id, guest_name: g.name, guest_handicap: g.handicap })),
    ];
    if (rows.length) {
      await getSupabase().from('game_participants').insert(rows);
    }
    return game as Game;
  },

  async join(gameId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const sb = getSupabase();
    const { error: rpcError } = await sb.rpc('join_game', { p_game_id: gameId });
    if (!rpcError) return;

    const { error } = await sb
      .from('game_participants')
      .insert({ game_id: gameId, member_id: me.id });
    if (error && error.code !== '23505') {
      throw new Error(formatJoinError(rpcError?.message, error.message));
    }
  },

  async start(gameId: UUID): Promise<void> {
    const { error } = await getSupabase()
      .from('games').update({ status: 'active' }).eq('id', gameId);
    if (error) throw error;
  },

  async participants(gameId: UUID): Promise<GameParticipant[]> {
    const { data, error } = await getSupabase()
      .from('game_participants').select('*').eq('game_id', gameId);
    if (error) throw error;
    return (data || []) as GameParticipant[];
  },

  async participantCounts(): Promise<Record<string, number>> {
    const { data, error } = await getSupabase()
      .from('game_participants').select('game_id');
    if (error) throw error;
    const counts: Record<string, number> = {};
    (data || []).forEach((row: { game_id: string }) => {
      counts[row.game_id] = (counts[row.game_id] || 0) + 1;
    });
    return counts;
  },

  async myGameIds(): Promise<UUID[]> {
    const me = await auth.me();
    if (!me) return [];
    const { data, error } = await getSupabase()
      .from('game_participants').select('game_id').eq('member_id', me.id);
    if (error) throw error;
    return (data || []).map((r) => r.game_id);
  },

  async updateConfig(gameId: UUID, config: Record<string, unknown>): Promise<void> {
    const { error } = await getSupabase()
      .from('games').update({ config }).eq('id', gameId);
    if (error) throw error;
  },

  async addParticipants(
    gameId: UUID,
    input: { members?: UUID[]; guests?: { name: string; handicap?: number }[] },
  ): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const game = await this.get(gameId);
    if (!game || game.organizer_id !== me.id) throw new Error('Only the organizer can invite players');
    const rows: Array<Record<string, unknown>> = [
      ...(input.members || []).map((member_id) => ({ game_id: gameId, member_id })),
      ...(input.guests || []).map((g) => ({ game_id: gameId, guest_name: g.name, guest_handicap: g.handicap ?? null })),
    ];
    if (!rows.length) return;
    const { error } = await getSupabase().from('game_participants').insert(rows);
    if (error && error.code !== '23505') throw error;
  },

  async cancel(gameId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('games').update({ status: 'cancelled' })
      .eq('id', gameId).eq('organizer_id', me.id);
    if (error) throw error;
  },

  async transferOrganizer(gameId: UUID, newOrganizerId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const sb = getSupabase();
    const { error } = await sb.from('games')
      .update({ organizer_id: newOrganizerId })
      .eq('id', gameId).eq('organizer_id', me.id);
    if (error) throw error;
    await sb.from('game_participants').upsert({ game_id: gameId, member_id: newOrganizerId });
  },

  async settle(
    gameId: UUID,
    results: { participantKey: string; final_score: number; net_score?: number; net_won_cents?: number; position?: number }[],
    settlement?: Record<string, unknown>,
  ): Promise<void> {
    const sb = getSupabase();
    for (const r of results) {
      const update: Record<string, unknown> = {
        final_score: r.final_score, position: r.position ?? null,
      };
      if (r.net_score != null) update.net_score = r.net_score;
      if (r.net_won_cents != null) update.net_won_cents = r.net_won_cents;
      await sb.from('game_participants').update(update)
        .eq('game_id', gameId).or(`member_id.eq.${r.participantKey},guest_name.eq.${r.participantKey}`);
    }
    const patch: Record<string, unknown> = { status: 'completed' };
    if (settlement) {
      const { data: game } = await sb.from('games').select('config').eq('id', gameId).single();
      patch.config = { ...(game?.config || {}), settlement };
    }
    await sb.from('games').update(patch).eq('id', gameId);
  },
};

// =====================================================================
// LIVE BETS (in-game side bets)
// =====================================================================

export const liveBets = {
  async listByGame(gameId: UUID): Promise<LiveBet[]> {
    const { data, error } = await getSupabase()
      .from('live_bets').select('*').eq('game_id', gameId).order('created_at');
    if (error) throw error;
    return (data || []) as LiveBet[];
  },

  async listByRound(roundId: UUID): Promise<LiveBet[]> {
    const { data, error } = await getSupabase()
      .from('live_bets').select('*').eq('round_id', roundId).order('created_at');
    if (error) throw error;
    return (data || []) as LiveBet[];
  },

  async propose(input: {
    game_id?: UUID;
    round_id?: UUID;
    type: string;
    terms: string;
    wager_cents?: number;
    opponent_id?: UUID;
  }): Promise<LiveBet> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('live_bets').insert({ proposer_id: me.id, ...input }).select().single();
    if (error) throw error;
    return data as LiveBet;
  },

  async resolve(id: UUID, winnerId: UUID | null, status: 'won' | 'lost' | 'pushed' | 'cancelled'): Promise<void> {
    const { error } = await getSupabase()
      .from('live_bets').update({
        winner_id: winnerId, status, resolved_at: new Date().toISOString(),
      }).eq('id', id);
    if (error) throw error;
  },
};

// =====================================================================
// TOURNAMENTS
// =====================================================================

export const tournaments = {
  async list(opts: { status?: string } = {}): Promise<Tournament[]> {
    let q = getSupabase().from('tournaments').select('*').order('starts_at', { ascending: false });
    if (opts.status) q = q.eq('status', opts.status);
    const { data, error } = await q;
    if (error) throw error;
    return ((data || []) as Tournament[]).map((t) => ({ ...t, config: t.config ?? {} }));
  },

  async get(id: UUID): Promise<Tournament | null> {
    const { data, error } = await getSupabase()
      .from('tournaments').select('*').eq('id', id).single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return { ...data, config: data.config ?? {} } as Tournament;
  },

  async create(input: Omit<Tournament, 'id' | 'created_at' | 'updated_at' | 'organizer_id' | 'club_id'>): Promise<Tournament> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('tournaments').insert({ organizer_id: me.id, club_id: me.club_id, ...input }).select().single();
    if (error) throw error;
    return data as Tournament;
  },

  async invite(tournamentId: UUID, memberIds: UUID[]): Promise<void> {
    if (!memberIds.length) return;
    const rows = memberIds.map(member_id => ({ tournament_id: tournamentId, member_id, status: 'invited' as const }));
    const { error } = await getSupabase().from('tournament_participants').insert(rows);
    if (error) throw error;
  },

  async join(tournamentId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('tournament_participants').upsert({
        tournament_id: tournamentId, member_id: me.id, status: 'joined',
        joined_at: new Date().toISOString(),
      });
    if (error) throw error;
  },

  async leaderboard(tournamentId: UUID): Promise<TournamentParticipant[]> {
    const { data, error } = await getSupabase()
      .from('tournament_participants').select('*')
      .eq('tournament_id', tournamentId).order('position', { ascending: true });
    if (error) throw error;
    return (data || []) as TournamentParticipant[];
  },

  async computeLeaderboard(tournamentId: UUID): Promise<Array<TournamentParticipant & { best_net?: number; best_gross?: number }>> {
    const tournament = await this.get(tournamentId);
    if (!tournament) return [];
    const { data: parts, error: pErr } = await getSupabase()
      .from('tournament_participants').select('*')
      .eq('tournament_id', tournamentId).eq('status', 'joined');
    if (pErr) throw pErr;
    const memberIds = (parts || []).map((p) => p.member_id);
    if (!memberIds.length) return [];

    let roundsQ = getSupabase().from('rounds').select('member_id, gross_total, net_total, played_at, posted_handicap')
      .in('member_id', memberIds).is('deleted_at', null).not('gross_total', 'is', null);
    if (tournament.starts_at) roundsQ = roundsQ.gte('played_at', tournament.starts_at);
    if (tournament.ends_at) roundsQ = roundsQ.lte('played_at', tournament.ends_at);
    const { data: rounds, error: rErr } = await roundsQ;
    if (rErr) throw rErr;

    const { data: members } = await getSupabase()
      .from('members').select('id, handicap').in('id', memberIds);

    const ranked = (parts || []).map((p) => {
      const mRounds = (rounds || []).filter((r) => r.member_id === p.member_id);
      const mHcp = members?.find((m) => m.id === p.member_id)?.handicap ?? 0;
      const allowance = tournament.handicap_allowance ?? 1;
      const playingHcp = Math.round((mHcp ?? 0) * allowance);
      const scored = mRounds.map((r) => {
        const net = tournament.scoring_basis === 'net'
          ? (r.net_total ?? (r.gross_total != null ? r.gross_total - playingHcp : null))
          : r.gross_total;
        return { gross: r.gross_total, net };
      }).filter((s) => s.net != null);
      const best = scored.length
        ? scored.reduce((a, b) => ((a.net as number) < (b.net as number) ? a : b))
        : null;
      return {
        ...p,
        best_gross: best?.gross ?? null,
        best_net: best?.net ?? null,
      };
    }).sort((a, b) => {
      const an = a.best_net ?? 999;
      const bn = b.best_net ?? 999;
      return an - bn;
    }).map((p, i) => ({ ...p, position: p.best_net != null ? i + 1 : null }));

    return ranked;
  },

  async myJoinedIds(): Promise<UUID[]> {
    const me = await auth.me();
    if (!me) return [];
    const { data, error } = await getSupabase()
      .from('tournament_participants').select('tournament_id')
      .eq('member_id', me.id).eq('status', 'joined');
    if (error) throw error;
    return (data || []).map((r) => r.tournament_id);
  },

  async leave(tournamentId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('tournament_participants').update({ status: 'withdrawn' })
      .eq('tournament_id', tournamentId).eq('member_id', me.id);
    if (error) throw error;
  },

  async cancel(tournamentId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('tournaments').update({ status: 'cancelled' })
      .eq('id', tournamentId).eq('organizer_id', me.id);
    if (error) throw error;
  },

  async transferOrganizer(tournamentId: UUID, newOrganizerId: UUID): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('tournaments').update({ organizer_id: newOrganizerId })
      .eq('id', tournamentId).eq('organizer_id', me.id);
    if (error) throw error;
    await this.invite(tournamentId, [newOrganizerId]);
  },

  async updateDescription(tournamentId: UUID, description: string | null): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { error } = await getSupabase()
      .from('tournaments').update({ description })
      .eq('id', tournamentId).eq('organizer_id', me.id);
    if (error) throw error;
  },

  async participants(tournamentId: UUID): Promise<TournamentParticipant[]> {
    const { data, error } = await getSupabase()
      .from('tournament_participants').select('*')
      .eq('tournament_id', tournamentId)
      .in('status', ['invited', 'joined']);
    if (error) throw error;
    return (data || []) as TournamentParticipant[];
  },
};

// =====================================================================
// CALL YOUR SHOT
// =====================================================================

export const callShots = {
  async list(opts: { playerId?: UUID; status?: string } = {}): Promise<CallYourShot[]> {
    let q = getSupabase().from('call_your_shots').select('*').order('proposed_at', { ascending: false });
    if (opts.playerId) q = q.eq('player_id', opts.playerId);
    if (opts.status) q = q.eq('status', opts.status);
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as CallYourShot[];
  },

  async viewerReactedShotIds(): Promise<Set<string>> {
    const me = await auth.me();
    if (!me) return new Set();
    const { data, error } = await getSupabase()
      .from('call_your_shot_reactions')
      .select('shot_id')
      .eq('member_id', me.id);
    if (error) throw error;
    return new Set((data || []).map((r: { shot_id: string }) => r.shot_id));
  },

  async propose(input: { course_id?: UUID; tee_id?: UUID; target_score: number; notes?: string }): Promise<CallYourShot> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    if (!input.course_id) throw new Error('Course is required to call your shot.');

    const courses = await db.courses.list();
    const course = courses.find((c) => c.id === input.course_id);
    if (!course) throw new Error('Course not found.');

    const tees = await db.courses.tees(input.course_id);
    const tee = input.tee_id
      ? tees.find((t) => t.id === input.tee_id)
      : tees.find((t) => t.name.toLowerCase().includes('white')) || tees[0];
    const expected = expectedScoreForCourse(me.handicap, course, tee);
    const target_score = clampCallTarget(input.target_score, expected);

    const { data, error } = await getSupabase()
      .from('call_your_shots')
      .insert({
        player_id: me.id,
        course_id: input.course_id,
        tee_id: input.tee_id ?? tee?.id ?? null,
        target_score,
        notes: input.notes ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as CallYourShot;
  },

  /** Per-stroke scoring: +10 per stroke better, -5 per stroke worse, 0 for exact. */
  async settle(shotId: UUID, actualScore: number): Promise<{ points: number; resolution: CallShotResolution }> {
    const sb = getSupabase();
    const { data: shot } = await sb.from('call_your_shots').select('*').eq('id', shotId).single();
    if (!shot) throw new Error('Shot not found');
    const delta = shot.target_score - actualScore;
    const points = delta > 0 ? delta * 10 : delta < 0 ? delta * 5 : 0;
    const resolution: CallShotResolution =
      delta >= 3 ? 'statement' : delta > 0 ? 'hit' : delta === 0 ? 'exact' : 'missed';
    const { error } = await sb.from('call_your_shots').update({
      actual_score: actualScore,
      points_awarded: points,
      resolution,
      status: 'completed',
      settled_at: new Date().toISOString(),
    }).eq('id', shotId);
    if (error) throw error;
    // Award points to ledger if non-zero
    if (points !== 0) {
      const reason = delta > 0
        ? `Beat Called Shot by ${delta} stroke${delta === 1 ? '' : 's'}`
        : `Missed Called Shot by ${-delta} stroke${delta === -1 ? '' : 's'}`;
      await sb.from('points_ledger').insert({
        member_id: shot.player_id, points, reason,
        source_type: 'call_your_shot', source_id: shotId,
      });
    }
    return { points, resolution };
  },

  /** Auto-settle a pending call when the member posts a matching round. */
  async tryAutoSettleAfterRoundPost(input: {
    course_id: UUID;
    gross_total: number;
    holes: HoleRange;
    played_at?: string;
  }): Promise<{
    settled: boolean;
    targetScore?: number;
    actualScore?: number;
    points?: number;
    resolution?: CallShotResolution;
  }> {
    const me = await auth.me();
    if (!me) return { settled: false };
    const holesKind = input.holes === 'full' ? 'full' as const : 'front-9' as const;
    if (!isValidCallActualScore(input.gross_total, holesKind)) return { settled: false };

    const pending = await this.list({ playerId: me.id, status: 'pending' });
    const shot = pickPendingCallToSettle(
      pending,
      me.id,
      input.course_id,
      input.played_at,
    );
    if (!shot) return { settled: false };

    const { points, resolution } = await this.settle(shot.id, input.gross_total);
    return {
      settled: true,
      targetScore: shot.target_score,
      actualScore: input.gross_total,
      points,
      resolution,
    };
  },

  async cancel(shotId: UUID): Promise<void> {
    const { error } = await getSupabase()
      .from('call_your_shots').update({ status: 'cancelled' }).eq('id', shotId);
    if (error) throw error;
  },

  /** React to someone else's call (social only; no points). */
  async react(shotId: UUID, type: 'eyes' | 'fire' | 'target'): Promise<void> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const sb = getSupabase();
    const { data: existing } = await sb.from('call_your_shot_reactions').select()
      .eq('shot_id', shotId).eq('member_id', me.id).eq('type', type).maybeSingle();
    if (existing) {
      await sb.from('call_your_shot_reactions').delete()
        .eq('shot_id', shotId).eq('member_id', me.id).eq('type', type);
    } else {
      await sb.from('call_your_shot_reactions').insert({ shot_id: shotId, member_id: me.id, type });
    }
  },
};

// =====================================================================
// ACES (Hole-in-One Wall)
// =====================================================================

export const aces = {
  /** Always visible to all club members (registry, ignores visibility). */
  async list(): Promise<Ace[]> {
    const { data, error } = await getSupabase()
      .from('aces').select('*').order('played_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Ace[];
  },

  async forMember(memberId: UUID): Promise<Ace[]> {
    const { data, error } = await getSupabase()
      .from('aces').select('*').eq('member_id', memberId).order('played_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Ace[];
  },

  async create(input: { course_id?: UUID; hole_num: number; played_at?: ISODateString; notes?: string; photo_url?: string }): Promise<Ace> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('aces').insert({
        member_id: me.id, played_at: input.played_at || new Date().toISOString(),
        ...input,
      }).select().single();
    if (error) throw error;
    // Award +500 points
    await getSupabase().from('points_ledger').insert({
      member_id: me.id, points: 500, reason: 'Hole-in-one',
      source_type: 'ace', source_id: data.id,
    });
    return data as Ace;
  },
};

// =====================================================================
// ACHIEVEMENTS
// =====================================================================

export const achievements = {
  async list(memberId: UUID, year?: number): Promise<Achievement[]> {
    let q = getSupabase().from('achievements').select('*').eq('member_id', memberId);
    if (year) q = q.eq('year', year);
    q = q.order('unlocked_at', { ascending: false });
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as Achievement[];
  },

  async unlock(achievementId: string, roundId?: UUID): Promise<Achievement | null> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('achievements').insert({
        member_id: me.id, achievement_id: achievementId,
        round_id: roundId, year: new Date().getFullYear(),
      }).select().single();
    if (error) {
      if (error.code === '23505') return null;       // already unlocked this year
      throw error;
    }
    return data as Achievement;
  },
};

// =====================================================================
// POINTS LEDGER
// =====================================================================

export const points = {
  async balance(memberId: UUID): Promise<number> {
    const { data, error } = await getSupabase()
      .from('v_points_totals').select('total_points').eq('member_id', memberId).maybeSingle();
    if (error) throw error;
    return data?.total_points ?? 0;
  },

  async ledger(memberId: UUID, limit = 50): Promise<PointsEntry[]> {
    const { data, error } = await getSupabase()
      .from('points_ledger').select('*').eq('member_id', memberId)
      .order('created_at', { ascending: false }).limit(limit);
    if (error) throw error;
    return (data || []) as PointsEntry[];
  },

  async awardBatch(awards: Array<{
    member_id: UUID;
    points: number;
    reason: string;
    source_type?: string;
    source_id?: UUID;
  }>): Promise<void> {
    if (!awards.length) return;
    const payload = awards.map((a) => ({
      member_id: a.member_id,
      points: a.points,
      reason: a.reason,
      source_type: a.source_type ?? 'achievement',
      source_id: a.source_id ?? null,
    }));
    const { error } = await getSupabase().rpc('award_round_points', { awards: payload });
    if (error) throw error;
  },

  async clubTotalsInRange(startAt?: string, endAt?: string): Promise<Array<{ member_id: UUID; total_points: number }>> {
    const { data, error } = await getSupabase().rpc('club_points_totals_range', {
      start_at: startAt ?? null,
      end_at: endAt ?? null,
    });
    if (error) throw error;
    return (data || []) as Array<{ member_id: UUID; total_points: number }>;
  },

  async totalInRange(memberId: UUID, startAt: string, endAt: string): Promise<number> {
    const { data, error } = await getSupabase()
      .from('points_ledger')
      .select('points')
      .eq('member_id', memberId)
      .gte('created_at', startAt)
      .lt('created_at', endAt);
    if (error) throw error;
    return (data || []).reduce((sum, row) => sum + (row.points || 0), 0);
  },
};

// =====================================================================
// CHARITY DONATIONS
// =====================================================================

export const donations = {
  async list(memberId: UUID): Promise<CharityDonation[]> {
    const { data, error } = await getSupabase()
      .from('charity_donations').select('*').eq('member_id', memberId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as CharityDonation[];
  },

  async totalCents(memberId: UUID): Promise<number> {
    const { data, error } = await getSupabase()
      .from('v_giving_totals').select('total_cents').eq('member_id', memberId).maybeSingle();
    if (error) throw error;
    return data?.total_cents ?? 0;
  },

  /** Log a self-reported donation. Bag Tag does not process money. */
  async log(input: { amount_cents: number; game_id?: UUID; round_id?: UUID; venmo_note?: string }): Promise<CharityDonation> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('charity_donations').insert({
        member_id: me.id, club_id: me.club_id, ...input,
      }).select().single();
    if (error) throw error;
    return data as CharityDonation;
  },
};

// =====================================================================
// NOTIFICATIONS
// =====================================================================

export const notifications = {
  async create(input: {
    recipient_id: UUID;
    type: Notification['type'];
    subject_type?: string | null;
    subject_id?: UUID | null;
    body?: string | null;
  }): Promise<void> {
    const me = await auth.me();
    if (!me) return;
    await insertNotification({ ...input, actor_id: me.id });
  },

  async list(opts: { unreadOnly?: boolean; limit?: number } = {}): Promise<Notification[]> {
    const me = await auth.me();
    if (!me) return [];
    let q = getSupabase().from('notifications').select('*')
      .eq('recipient_id', me.id)
      .order('created_at', { ascending: false });
    if (opts.unreadOnly) q = q.is('read_at', null);
    q = q.limit(opts.limit ?? 50);
    const { data, error } = await q;
    if (error) throw error;
    return (data || []) as Notification[];
  },

  async markRead(id: UUID): Promise<void> {
    const { error } = await getSupabase()
      .from('notifications').update({ read_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
  },

  async markAllRead(): Promise<void> {
    const me = await auth.me();
    if (!me) return;
    const { error } = await getSupabase()
      .from('notifications').update({ read_at: new Date().toISOString() })
      .eq('recipient_id', me.id).is('read_at', null);
    if (error) throw error;
  },

  async unreadCount(): Promise<number> {
    const me = await auth.me();
    if (!me) return 0;
    const { count, error } = await getSupabase()
      .from('notifications').select('*', { count: 'exact', head: true })
      .eq('recipient_id', me.id).is('read_at', null);
    if (error) throw error;
    return count ?? 0;
  },

  subscribe(cb: (notif: Notification) => void): () => void {
    const ch = getSupabase()
      .channel('notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' },
          (payload) => cb(payload.new as Notification))
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications' },
          (payload) => cb(payload.new as Notification))
      .subscribe();
    return () => { getSupabase().removeChannel(ch); };
  },
};

// =====================================================================
// SUGGESTIONS (suggestion box)
// =====================================================================

export const suggestions = {
  async submit(input: { category: SuggestionCategory; body: string }): Promise<Suggestion> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const { data, error } = await getSupabase()
      .from('suggestions').insert({ author_id: me.id, ...input }).select().single();
    if (error) throw error;
    return data as Suggestion;
  },

  /** Member sees their own submissions. Admins see all. RLS handles this. */
  async list(): Promise<Suggestion[]> {
    const { data, error } = await getSupabase()
      .from('suggestions').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Suggestion[];
  },

  async update(
    id: UUID,
    input: { status?: SuggestionStatus; admin_notes?: string | null },
  ): Promise<Suggestion> {
    const me = await auth.me();
    if (!me) throw new Error('Not authenticated');
    const patch: Record<string, unknown> = { ...input };
    if (input.status) {
      patch.reviewed_by = me.id;
      patch.reviewed_at = new Date().toISOString();
    }
    const { data, error } = await getSupabase()
      .from('suggestions').update(patch).eq('id', id).select().single();
    if (error) throw error;
    return data as Suggestion;
  },
};

// =====================================================================
// COURSES (reference data)
// =====================================================================

export const courses = {
  async list(opts: { includeTest?: boolean } = {}): Promise<Course[]> {
    const { data, error } = await getSupabase()
      .from('courses').select('*').order('name');
    if (error) throw error;
    const rows = (data || []) as Course[];
    if (opts.includeTest) return rows;
    return filterProductionCourses(rows);
  },

  async tees(courseId: UUID): Promise<CourseTee[]> {
    const { data, error } = await getSupabase()
      .from('course_tees').select('*').eq('course_id', courseId);
    if (error) throw error;
    return (data || []) as CourseTee[];
  },

  async holes(courseId: UUID): Promise<CourseHole[]> {
    const { data, error } = await getSupabase()
      .from('course_holes').select('*').eq('course_id', courseId)
      .order('hole_num');
    if (error) throw error;
    return (data || []) as CourseHole[];
  },
};

// =====================================================================
// CLUBS
// =====================================================================

export const clubs = {
  async get(id: UUID): Promise<Club | null> {
    const { data, error } = await getSupabase()
      .from('clubs').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data as Club | null;
  },
};

// =====================================================================
// INVITATIONS
// =====================================================================

export const invitations = {
  async list(): Promise<Invitation[]> {
    const me = await auth.me();
    if (!me) return [];
    const { data, error } = await getSupabase()
      .from('invitations')
      .select('*')
      .eq('inviter_id', me.id)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Invitation[];
  },

  async send(input: {
    email?: string;
    phone?: string;
    recipient_name?: string;
    personal_note?: string;
  }): Promise<{ invitation: Invitation; claim_link: string; dispatched: boolean }> {
    return callEdgeFunction('send-invitation', input);
  },

  async cancel(id: UUID): Promise<void> {
    const { error } = await getSupabase()
      .from('invitations')
      .update({ status: 'cancelled' })
      .eq('id', id);
    if (error) throw error;
  },

  async resend(id: UUID): Promise<void> {
    await callEdgeFunction('send-invitation', { invitation_id: id });
  },
};

// =====================================================================
// MAIN EXPORT
// =====================================================================

export const db = {
  auth, members, follows, posts, likes, wagers, roundTags, reactions, comments,
  rounds, games, groupRounds, liveBets, tournaments, callShots,
  aces, achievements, points, donations, notifications,
  suggestions, courses, clubs, invitations,
};

export default db;

export { groupRounds, playerKeyFor };

// Re-export types for convenience
export type * from './types';
